import React from "react";
import { Sparkles } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";
import { BrandMark } from "@/components/BrandMark";

/**
 * <ProGate feature="journal_insights">…</ProGate>
 *
 * Renders `children` when the caller is entitled; otherwise renders a
 * calm "Go deeper" upsell card. Never blocks the underlying page — just
 * substitutes its own content in place.
 *
 * `title` and `blurb` describe the specific feature (used both in the
 * inline card and in the pricing modal that opens on click).
 */
export default function ProGate({
  feature,
  title = "Go deeper into your journey.",
  blurb = "You already have what you need to begin. Pro adds depth — evolving insights that connect your Compass to your rituals, personalized guidance that adapts as you change, and reflections that reveal the patterns beneath the surface.",
  bullets,
  children,
  fallback,
  variant = "card",     // "card" | "inline" | "silent"
  testid,
}) {
  const { has, loading, openPricing } = useSubscription();

  if (loading) return null;
  if (has(feature)) return <>{children}</>;
  if (variant === "silent") return fallback ?? null;

  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={() => openPricing({ feature, title, blurb, bullets })}
        data-testid={testid || `pro-gate-inline-${feature}`}
        className="inline-flex items-center gap-2 text-sm text-[hsl(var(--terracotta))] hover:text-[hsl(var(--fg))] tab-link border-b border-dashed border-[hsl(var(--terracotta))]/60"
      >
        <Sparkles size={13} /> {title}
      </button>
    );
  }

  return (
    <section
      data-testid={testid || `pro-gate-${feature}`}
      className="relative rounded-lg border hairline bg-gradient-to-br from-[hsl(var(--sand))]/60 to-[hsl(var(--paper))] p-7 md:p-8 reveal"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
        <Sparkles size={12} />
        <span className="inline-flex items-center gap-1.5"><BrandMark className="text-sm" signOnMount={false} testid={`pro-gate-brand-${feature}`} /> Pro</span>
      </div>
      <h3
        className="font-display text-2xl md:text-3xl mt-2 leading-tight tracking-tight"
        data-testid={`pro-gate-title-${feature}`}
      >
        {title}
      </h3>
      <p className="mt-2 max-w-2xl text-[15px] text-[hsl(var(--fg))]/85 leading-relaxed">
        {blurb}
      </p>
      {bullets && bullets.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-sm text-[hsl(var(--fg))]/85 max-w-lg">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--terracotta))]/70" aria-hidden />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => openPricing({ feature, title, blurb, bullets })}
          data-testid={`pro-gate-cta-${feature}`}
          className="rounded-full px-5 py-2.5 bg-[hsl(var(--terracotta))] text-[hsl(var(--paper))] text-sm hover:bg-[hsl(var(--terracotta))]/90 tab-link"
        >
          Go Deeper
        </button>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">
          Your foundation is yours. Pro is an invitation to go deeper.
        </span>
      </div>
    </section>
  );
}
